import { useEffect, useRef, useState } from "react";
import { brand } from "./theme/brand";
import { App as CapApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { supabase, supabaseConfigured } from "./supabase/client";
import { isNative, isRealMobilePhone } from "./lib/platform";
import {
  clearCompletedPasswordRecoveryEmail,
  getCompletedPasswordRecoveryEmail,
  setCompletedPasswordRecoveryEmail,
} from "./supabase/passwordRecoveryState";
import {
  parseRedirectAuthState,
  parseOuraRedirectState,
  clearRedirectFlags,
} from "./lib/deeplinks";
import WeightTracker from "./WeightTracker";
import DailyEntryMobile from "./components/DailyEntryMobile";

const LOCAL_ANON_STORAGE_KEYS = [
  "weight-tracker-entries",
  "weight-tracker-vitals",
  "weight-tracker-forecast",
  "weight-tracker-sprints",
  "weight-tracker-roadmap",
  "weight-tracker-roadmap-updated-at",
];

// Mirrors ONBOARDING_HANDOFF_KEY in src/WeightTracker.tsx. Deliberately kept
// out of LOCAL_ANON_STORAGE_KEYS so the onboarding plan survives the wipe on
// SIGNED_IN. Cleared explicitly on SIGNED_OUT so User A's plan can't leak to
// User B on the same device.
const ONBOARDING_HANDOFF_KEY = "weight-tracker-onboarding-handoff";

function clearAnonymousLocalState() {
  if (typeof window === "undefined") return;
  try {
    for (const key of LOCAL_ANON_STORAGE_KEYS) window.localStorage.removeItem(key);
  } catch {
    // ignore storage failures
  }
}

function clearOnboardingHandoffLocal() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(ONBOARDING_HANDOFF_KEY);
  } catch {
    // ignore
  }
}



export default function App() {
  const [userId, setUserId] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [redirectNotice, setRedirectNotice] = useState<string | null>(null);
  const [redirectEmail, setRedirectEmail] = useState<string | null>(null);
  const [redirectAuthMode, setRedirectAuthMode] = useState<"signin" | "signup" | "updatePassword" | null>(null);
  const [passwordRecoveryActive, setPasswordRecoveryActive] = useState(false);
  const [authSuccessNotice, setAuthSuccessNotice] = useState<string | null>(null);
  const [ouraConnectedNotice, setOuraConnectedNotice] = useState<boolean>(false);
  const [ouraErrorNotice, setOuraErrorNotice] = useState<string | null>(null);
  // If Supabase isn't configured, skip auth entirely and render immediately
  const [authReady, setAuthReady] = useState(!supabaseConfigured);
  const [isLandscapeMobile, setIsLandscapeMobile] = useState(false);
  const isResetPasswordRoute = typeof window !== "undefined" && window.location.pathname === "/reset-password";
  const redirectAuthModeRef = useRef<typeof redirectAuthMode>(redirectAuthMode);
  const redirectEmailRef = useRef<string | null>(redirectEmail);
  const passwordRecoveryActiveRef = useRef(passwordRecoveryActive);

  useEffect(() => {
    redirectAuthModeRef.current = redirectAuthMode;
  }, [redirectAuthMode]);

  useEffect(() => {
    redirectEmailRef.current = redirectEmail;
  }, [redirectEmail]);

  useEffect(() => {
    passwordRecoveryActiveRef.current = passwordRecoveryActive;
  }, [passwordRecoveryActive]);

  // ── Web: parse redirect flags from the current URL on mount ──────────────
  useEffect(() => {
    if (isNative) return; // Native uses appUrlOpen listener below

    const { verified, recovery, email, authError, authErrorCode } = parseRedirectAuthState(
      window.location.search,
      window.location.hash,
    );
    const { connected, error } = parseOuraRedirectState(window.location.search);

    if (authError) {
      // Supabase returned an error on the email callback (e.g. expired link).
      // Show a helpful message and clear the dirty URL so the SDK doesn't
      // re-process it on refresh and leave the app in a broken state.
      if (recovery) {
        setRedirectAuthMode("signin");
        setRedirectNotice(
          authErrorCode === "otp_expired"
            ? "Your password reset link has expired. Request a new reset email to continue."
            : "Your password reset link is invalid or has already been used. Request a new reset email to continue.",
        );
      } else if (authErrorCode === "otp_expired") {
        setRedirectAuthMode("signup");
        setRedirectNotice("Your email verification link has expired. Please sign up again to get a new one.");
      } else {
        setRedirectAuthMode("signin");
        setRedirectNotice("The email link is invalid or has already been used. Please sign in or request a new reset link.");
      }
      setRedirectEmail(email);
    } else if (verified) {
      clearCompletedPasswordRecoveryEmail();
      setPasswordRecoveryActive(false);
      setRedirectAuthMode("signin");
      setRedirectNotice("Email verified. Please sign in to continue.");
      setRedirectEmail(email);
    } else if (recovery) {
      clearCompletedPasswordRecoveryEmail();
      setRedirectEmail(email);
      setRedirectNotice(null);
      setRedirectAuthMode(null);
      setPasswordRecoveryActive(true);
    }
    if (connected) setOuraConnectedNotice(true);
    if (error) setOuraErrorNotice(decodeURIComponent(error));
    // Always clear the URL when we detect any auth-related params — including
    // error params — so the Supabase SDK doesn't re-process them on refresh.
    if (verified || recovery || authError || connected || error) clearRedirectFlags();
  }, []);

  // ── Native: listen for deep-link URLs (com.weightplanner.app://...) ───────
  // Fires both when the app is opened cold from a deep link and when it's
  // already running and brought to foreground via a deep link.
  useEffect(() => {
    if (!isNative) return;

    const listenerPromise = CapApp.addListener("appUrlOpen", (event) => {
      // Dismiss the in-app browser immediately so the user isn't left on a
      // browser error page after the custom-scheme redirect fires.
      Browser.close();
      try {
        const u = new URL(event.url);
        const { verified, recovery, email, authError, authErrorCode } = parseRedirectAuthState(u.search, u.hash);
        const { connected, error } = parseOuraRedirectState(u.search);

        // PKCE code from magic-link / email confirmation. Native deep links
        // bypass Supabase's auto-URL detection, so exchange it manually here.
        // onAuthStateChange will fire SIGNED_IN once the session is created.
        const code = u.searchParams.get("code");
        if (code && !authError) {
          void supabase.auth.exchangeCodeForSession(code).catch(() => {
            setRedirectAuthMode("signin");
            setRedirectNotice("This sign-in link has expired or already been used. Please request a new one.");
            setRedirectEmail(email);
          });
          // Don't fall through to "verified — please sign in" copy; the
          // exchange will sign the user in automatically.
          return;
        }

        if (authError) {
          if (recovery) {
            setRedirectAuthMode("signin");
            setRedirectNotice(
              authErrorCode === "otp_expired"
                ? "Your password reset link has expired. Request a new reset email to continue."
                : "Your password reset link is invalid or has already been used. Request a new reset email to continue.",
            );
          } else if (authErrorCode === "otp_expired") {
            setRedirectAuthMode("signup");
            setRedirectNotice("Your email verification link has expired. Please sign up again to get a new one.");
          } else {
            setRedirectAuthMode("signin");
            setRedirectNotice("The email link is invalid or has already been used. Please sign in or request a new reset link.");
          }
          setRedirectEmail(email);
        } else if (verified) {
          clearCompletedPasswordRecoveryEmail();
          setPasswordRecoveryActive(false);
          setRedirectAuthMode("signin");
          setRedirectNotice("Email verified. Please sign in to continue.");
          setRedirectEmail(email);
        } else if (recovery) {
          clearCompletedPasswordRecoveryEmail();
          setRedirectEmail(email);
          setRedirectNotice(null);
          setRedirectAuthMode(null);
          setPasswordRecoveryActive(true);
        }
        if (connected) setOuraConnectedNotice(true);
        if (error) setOuraErrorNotice(decodeURIComponent(error));
      } catch {
        // Malformed URL — ignore
      }
    });

    return () => {
      listenerPromise.then((l) => l.remove());
    };
  }, []);

  // ── Supabase auth state ───────────────────────────────────────────────────
  useEffect(() => {
    if (!supabaseConfigured) return;

    // Restore session on load (handles PKCE code exchange from deep link too).
    // The .catch() ensures authReady is always set even if the PKCE exchange
    // fails or throws — otherwise the app would stay blank indefinitely.
    // The timeout is a last-resort fallback in case getSession() never resolves
    // (e.g. network hang during PKCE exchange).
    let didSetReady = false;
    const authReadyTimeout = setTimeout(() => {
      if (!didSetReady) setAuthReady(true);
    }, 8000);

    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        didSetReady = true;
        clearTimeout(authReadyTimeout);
        const completedRecoveryEmail = getCompletedPasswordRecoveryEmail();

        if (completedRecoveryEmail) {
          setUserId(null);
          setUserEmail(null);
          setPasswordRecoveryActive(false);
          setRedirectAuthMode("signin");
          setRedirectNotice("Password updated successfully. Please sign in with your new password.");
          setRedirectEmail(completedRecoveryEmail ?? session?.user?.email ?? redirectEmailRef.current ?? null);
          if (typeof window !== "undefined" && window.location.pathname === "/reset-password") {
            window.history.replaceState({}, "", "/");
          }
          if (session) {
            void supabase.auth.signOut();
          }
          setAuthReady(true);
          return;
        }

        setUserId(session?.user?.id ?? null);
        setUserEmail(session?.user?.email ?? null);
        if (isResetPasswordRoute && session) {
          setRedirectEmail(session.user.email ?? redirectEmailRef.current ?? null);
          setRedirectNotice(null);
          setRedirectAuthMode(null);
          setPasswordRecoveryActive(true);
        }
        setAuthReady(true);
      })
      .catch(() => {
        // Session check failed (e.g. PKCE code already consumed or network error).
        // Render the app in logged-out state so the user is never stuck on a blank page.
        didSetReady = true;
        clearTimeout(authReadyTimeout);
        setAuthReady(true);
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      console.info("[auth] onAuthStateChange", { event, hasSession: !!session });
      setUserId(session?.user?.id ?? null);
      setUserEmail(session?.user?.email ?? null);

      if (event === "PASSWORD_RECOVERY") {
        clearCompletedPasswordRecoveryEmail();
        setRedirectEmail(session?.user?.email ?? redirectEmailRef.current ?? null);
        setRedirectNotice(null);
        setRedirectAuthMode(null);
        setPasswordRecoveryActive(true);
        setAuthSuccessNotice(null);
        return;
      }

      if (event === "SIGNED_IN") {
        const completedRecoveryEmail = getCompletedPasswordRecoveryEmail();
        if (completedRecoveryEmail) {
          setUserId(null);
          setUserEmail(null);
          setPasswordRecoveryActive(false);
          setRedirectAuthMode("signin");
          setRedirectNotice("Password updated successfully. Please sign in with your new password.");
          setRedirectEmail(completedRecoveryEmail ?? session?.user?.email ?? redirectEmailRef.current ?? null);
          setAuthSuccessNotice(null);
          if (session) {
            void supabase.auth.signOut();
          }
          return;
        }

        const keepRecoveryMode = isResetPasswordRoute || passwordRecoveryActiveRef.current || redirectAuthModeRef.current === "updatePassword";

        if (keepRecoveryMode) {
          setAuthSuccessNotice(null);
          setRedirectEmail(session?.user?.email ?? redirectEmailRef.current ?? null);
          setRedirectNotice(null);
          setRedirectAuthMode(null);
          setPasswordRecoveryActive(true);
          return;
        }

        // Prevent anonymous/local dataset from contaminating authenticated state.
        clearAnonymousLocalState();
        setAuthSuccessNotice("Signed in successfully.");
        setRedirectNotice(null);
        setRedirectAuthMode(null);
        setRedirectEmail(null);
      }

      if (event === "SIGNED_OUT") {
        // Drop any stashed onboarding handoff so a different user signing in
        // on the same device can't inherit the previous user's plan.
        clearOnboardingHandoffLocal();
      }
    });

    return () => {
      subscription.unsubscribe();
      clearTimeout(authReadyTimeout);
    };
  }, []);


  // Enforce portrait orientation for mobile web (best effort).
  useEffect(() => {
    if (typeof window === "undefined") return;

    const updateLandscapeState = () => {
      const isLandscape = window.matchMedia("(orientation: landscape)").matches;
      setIsLandscapeMobile(isRealMobilePhone() && isLandscape);
    };

    const lockToPortrait = async () => {
      const orientationApi = (screen as Screen & { orientation?: { lock?: (orientation: string) => Promise<void> } }).orientation;
      if (!orientationApi?.lock) return;
      try {
        await orientationApi.lock("portrait");
      } catch {
        // Some browsers (notably iOS Safari) block orientation lock API.
      }
    };

    updateLandscapeState();
    void lockToPortrait();

    const onResize = () => updateLandscapeState();
    const onOrientation = () => {
      updateLandscapeState();
      void lockToPortrait();
    };

    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onOrientation);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onOrientation);
    };
  }, []);

  // Show a minimal dark splash while the Supabase session is being restored (or while
  // a PKCE code-exchange round-trip is in flight after an email confirmation click).
  // This prevents the app from appearing blank / white-screening during that window.
  if (!authReady) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: brand.background.primary,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        aria-label="Loading"
      />
    );
  }

  if (typeof window !== "undefined") {
    const path = window.location.pathname;
    const preview = new URLSearchParams(window.location.search).get("preview");
    if (path === "/secret/daily-entry-mobile" || preview === "daily-entry-mobile")
      return <DailyEntryMobile userEmail={userEmail} />;
  }

  const handlePasswordRecoveryComplete = async (email: string | null) => {
    const nextEmail = email ?? redirectEmailRef.current ?? userEmail ?? null;
    setCompletedPasswordRecoveryEmail(nextEmail);
    setPasswordRecoveryActive(false);
    passwordRecoveryActiveRef.current = false;
    setRedirectAuthMode("signin");
    setRedirectNotice("Password updated successfully. Please sign in with your new password.");
    setRedirectEmail(nextEmail);
    redirectAuthModeRef.current = "signin";
    setUserId(null);
    setUserEmail(null);
    setAuthSuccessNotice(null);
    if (typeof window !== "undefined" && window.location.pathname === "/reset-password") {
      window.history.replaceState({}, "", "/");
    }
    await supabase.auth.signOut();
  };

  const effectiveUserId = passwordRecoveryActive ? null : userId;
  const effectiveUserEmail = passwordRecoveryActive ? null : userEmail;

  return (
    <>
      <WeightTracker
        userId={effectiveUserId}
        userEmail={effectiveUserEmail}
        verifiedEmailNotice={redirectNotice}
        verifiedEmailPrefill={redirectEmail}
        redirectAuthMode={redirectAuthMode}
        passwordRecoveryActive={passwordRecoveryActive}
        authSuccessNotice={authSuccessNotice}
        onClearAuthSuccessNotice={() => setAuthSuccessNotice(null)}
        onClearVerifiedEmailNotice={() => { setRedirectNotice(null); setRedirectAuthMode(null); setRedirectEmail(null); }}
        onPasswordRecoveryComplete={handlePasswordRecoveryComplete}
        ouraConnectedNotice={ouraConnectedNotice}
        ouraErrorNotice={ouraErrorNotice}
        onClearOuraNotices={() => {
          setOuraConnectedNotice(false);
          setOuraErrorNotice(null);
        }}
      />
      {isLandscapeMobile ? (
        <div
          role="alert"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 30000,
            display: "grid",
            placeItems: "center",
            padding: "1.5rem",
            background: brand.background.primary,
            color: "white",
            textAlign: "center",
          }}
        >
          <div style={{ display: "grid", gap: "0.5rem", maxWidth: 420 }}>
            <div style={{ fontSize: "1.15rem", fontWeight: 800 }}>Portrait only</div>
            <div style={{ fontSize: "0.9rem", color: "rgba(255,255,255,0.78)" }}>
              Rotate your device back to portrait to continue using WeightPlanner Pro.
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
